import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { formatMoney } from './claimDetailUtils';
import type { TBenefitItem, TUtilization } from './claimDetailTypes';

interface IUtilizationPanelProps {
  utilization: TUtilization[];
  benefitItems: TBenefitItem[];
  currency: string;
}

export function UtilizationPanel({ utilization, benefitItems, currency }: IUtilizationPanelProps) {
  const rows = useMemo(
    () =>
      utilization
        .map(u => {
          const item = benefitItems.find(b => b._id === u.benefitItemId);
          if (!item) return null;
          const limit = item.annualLimit ?? 0;
          const pct = limit > 0 ? Math.min((u.amountUsed / limit) * 100, 100) : 0;
          return { ...u, item, limit, pct };
        })
        .filter(Boolean),
    [utilization, benefitItems]
  );

  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Benefit Utilization</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.map(row => {
          if (!row) return null;
          return (
            <div key={row._id} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{row.item.name}</span>
                <span className="text-muted-foreground">
                  {formatMoney(row.amountUsed, currency)} / {row.limit > 0 ? formatMoney(row.limit, currency) : '∞'}
                </span>
              </div>
              {row.limit > 0 && <Progress value={row.pct} className="h-2" />}
              {row.item.visitLimit != null && (
                <p className="text-xs text-muted-foreground">
                  {row.visitsUsed} of {row.item.visitLimit} visits used
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
